import { useFormikContext } from 'formik'
import { ElementType, FC } from 'react'
import { useTranslation } from 'react-i18next'

import { IconProps } from '@icons/IconProps'

import { Body1 } from '@ui/Typography'
import { CheckBox } from '@ui/v2/CheckBox/CheckBox'
import { Input } from '@ui/v2/Input/Input'
import { ModalContentWrapper } from '@ui/v2/Modal/ModalContentWrapper'

import { extractValueFromFilterQuery } from '@utils/extractValueFromFilterQuery'
import { isAllowedNumericKey } from '@utils/input.utils'

import { FilterQueryProps } from '@global/filter/FilterQueryModal'

import { FromTo } from '../interfaces/fromTo.interface'
import { RangeWrapper } from './RangeWrapper'

import styles from './RangeModalContent.module.scss'

export interface CheckBoxProps {
  label: string
  key: string
  checkedValue?: string
  uncheckedValue?: string
}

interface RangeModalContentProps {
  name: string
  keys: FromTo
  placeholders?: FromTo
  checkBox?: CheckBoxProps
  InputIcon?: ElementType<IconProps>
  handleClose: () => void
}

export const RangeModalContent: FC<RangeModalContentProps> = (props) => {
  const { t } = useTranslation()
  const {
    name,
    keys,
    placeholders = { from: t('From'), to: t('To') },
    checkBox,
    InputIcon,
    handleClose,
  } = props

  const { setFieldValue, setValues, errors, values, handleSubmit, setErrors } =
    useFormikContext<FilterQueryProps>()

  const handleChange = (key: string, value: string) => {
    setFieldValue(key, value)

    if (errors[keys.to]) {
      setErrors({})
    }
  }

  const handleCheckBoxChange = (key: string, value?: string) => {
    setValues({
      [key]: value,
    })
    setErrors({})
  }

  const checkBoxValue = checkBox ? (values[checkBox?.key] as string) : undefined
  const isChecked = !!checkBox && checkBoxValue === checkBox.checkedValue

  const hasErrors = Object.keys(errors).length > 0
  const isDisabled = hasErrors || (!values[keys.from] && !values[keys.to] && !isChecked)

  return (
    <ModalContentWrapper
      title={name}
      isApplyDisabled={isDisabled}
      onClose={handleClose}
      onConfirm={handleSubmit}
      onCancel={handleClose}
    >
      <RangeWrapper
        leftInput={
          <Input
            value={extractValueFromFilterQuery(values[keys.from])}
            placeholder={placeholders.from}
            onChange={(e) => handleChange(keys.from, e.target.value)}
            endIcon={InputIcon}
            hint={errors[keys.from]}
            error={!!errors[keys.from]}
            onKeyDown={(e) => !isAllowedNumericKey(e) && e.preventDefault()}
            disabled={isChecked}
            type='number'
          />
        }
        rightInput={
          <Input
            value={extractValueFromFilterQuery(values[keys.to])}
            placeholder={placeholders.to}
            onChange={(e) => handleChange(keys.to, e.target.value)}
            endIcon={InputIcon}
            hint={errors[keys.to]}
            error={!!errors[keys.to]}
            onKeyDown={(e) => !isAllowedNumericKey(e) && e.preventDefault()}
            disabled={isChecked}
            type='number'
          />
        }
      />
      {checkBox && (
        <div
          className={styles.checkBox}
          onClick={() =>
            handleCheckBoxChange(
              checkBox.key,
              isChecked ? checkBox.uncheckedValue : checkBox.checkedValue
            )
          }
        >
          <CheckBox value={isChecked} />

          <Body1>{checkBox.label}</Body1>
        </div>
      )}
    </ModalContentWrapper>
  )
}
